import { useState, useCallback } from 'react'
import { chatService } from '../services/chatService'
import type { Message } from '../components/chat/ChatMessage'

export const useChat = () => {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const generateId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  const sendMessage = useCallback(async (content: string, provider?: { provider: string; model: string }, debugMode = false) => {
    if (!content.trim()) return

    const userMessage: Message = {
      id: generateId(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    try {
      const response = await chatService.sendMessage(content, provider, debugMode)
      
      const assistantMessage: Message = {
        id: generateId(),
        content: response.content,
        role: 'assistant',
        timestamp: new Date(),
        provider: response.provider
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (error) {
      console.error('Chat error:', error)
      
      const errorMessage: Message = {
        id: generateId(),
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        provider: provider,
        error: error instanceof Error ? error.message : 'Failed to send message'
      }

      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>) => {
    const fullMessage: Message = {
      ...message,
      id: generateId(),
      timestamp: new Date()
    }
    setMessages(prev => [...prev, fullMessage])
  }, [])

  return {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    addMessage
  }
}